import React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Button, SegmentedControl } from '@mantine/core';

import { AnnotateControls } from './common/AnnotateControls';
import {
  selectChainType,
  selectHighlightedRegions,
  selectShowFWR,
  setChainType,
  setShowFWR,
  toggleHighlightedRegion,
} from '../../store/slices/antibodyAnalysis.slice';
import type { AppDispatch } from '../../store/store';

type CdrStyle = { bg: string; text: string; border: string };

// Per-CDR colours match the sequence region tints in SequenceWithRegions
const CDR_ACTIVE_STYLE: Record<'cdr1' | 'cdr2' | 'cdr3', CdrStyle> = {
  cdr1: { bg: '#c2ecdf', text: '#0f766e', border: '#0d9488' },
  cdr2: { bg: '#d9c9ff', text: '#7e22ce', border: '#9333ea' },
  cdr3: { bg: '#ffe6bd', text: '#b45309', border: '#d97706' },
};

const FWR_ACTIVE_STYLE: CdrStyle = { bg: '#e2f2ff', text: '#0064B0', border: '#0064B0' };
const INACTIVE_STYLE = { backgroundColor: '#969eae', color: 'white', border: 'none' };

// Interleaved in IMGT order so buttons match the left-to-right sequence display
const REGION_BUTTONS = [
  { key: 'fwr1', label: 'FWR1', isCDR: false, locked: false },
  { key: 'cdr1', label: 'CDR1', isCDR: true, locked: false },
  { key: 'fwr2', label: 'FWR2', isCDR: false, locked: false },
  { key: 'cdr2', label: 'CDR2', isCDR: true, locked: false },
  { key: 'fwr3', label: 'FWR3', isCDR: false, locked: false },
  { key: 'cdr3', label: 'CDR3', isCDR: true, locked: true },
  { key: 'fwr4', label: 'FWR4', isCDR: false, locked: false },
] as const;

const CHAIN_DATA = [
  { label: 'All', value: 'all' },
  { label: 'Heavy', value: 'heavy' },
  { label: 'Light', value: 'light' },
];

const s = {
  bar: 'flex items-center gap-4 px-4 py-1 bg-[rgba(235,246,242,0.35)] border-b border-[#e1e5e9]',
  divider: 'text-[#c0c8d0] select-none',
  regionGroup: 'flex items-center gap-1.5',
  cdrGroup: 'flex items-center gap-1.5',
  rightGroup: 'flex items-center gap-4 ml-auto',
  chainRoot: 'bg-white border border-[#969eae] rounded-[8px] p-0.5',
  chainIndicator: 'bg-[#969eae] rounded-[6px] shadow-none',
  chainLabel: 'text-sm font-normal px-4 py-1 text-foreground data-[active]:text-white',
} as const;

/**
 * SequenceControlsBar — Annotate checkboxes, CDR / FWR region toggles, Show/Hide FWR, and the chain
 * selector. Split out from ControlBar (separation of concerns, not the page/selection
 * controls) so the controls that change how sequences render live directly above the
 * table they affect.
 */
export function SequenceControlsBar() {
  const dispatch = useDispatch<AppDispatch>();
  const showFWR = useSelector(selectShowFWR);
  const chainType = useSelector(selectChainType);
  const highlightedRegions = useSelector(selectHighlightedRegions);

  return (
    <div className={s.bar}>
      <AnnotateControls />
      <span className={s.divider}>|</span>

      <div className={s.regionGroup}>
        {/* Interleaved FWR/CDR buttons in IMGT order: FWR1 CDR1 FWR2 CDR2 FWR3 CDR3 FWR4 */}
        <div className={s.cdrGroup}>
          {REGION_BUTTONS.filter((region) => region.isCDR || showFWR).map((region) => {
            const isActive = highlightedRegions.includes(region.key);
            let style: React.CSSProperties;
            if (region.isCDR) {
              const cdrStyle = CDR_ACTIVE_STYLE[region.key as 'cdr1' | 'cdr2' | 'cdr3'];
              style = {
                ...(isActive ? { backgroundColor: cdrStyle.bg, color: cdrStyle.text, outline: `1px solid ${cdrStyle.border}` } : INACTIVE_STYLE),
                ...(region.locked ? { cursor: 'not-allowed' } : {}),
              };
            } else {
              style = isActive
                ? { backgroundColor: FWR_ACTIVE_STYLE.bg, color: FWR_ACTIVE_STYLE.text, outline: `1px solid ${FWR_ACTIVE_STYLE.border}` }
                : INACTIVE_STYLE;
            }
            return (
              <Button
                key={region.key}
                size="compact-xs"
                variant="filled"
                style={style}
                onClick={() => !region.locked && dispatch(toggleHighlightedRegion(region.key))}
              >
                {region.label}
              </Button>
            );
          })}
        </div>

        <Button
          size="compact-xs"
          variant={showFWR ? 'filled' : 'outline'}
          style={showFWR ? INACTIVE_STYLE : { borderColor: '#969eae', color: '#3d3d3d' }}
          onClick={() => dispatch(setShowFWR(!showFWR))}
        >
          {showFWR ? 'Hide FWR' : 'Show FWR'}
        </Button>
      </div>

      <div className={s.rightGroup}>
        <SegmentedControl
          size="xs"
          value={chainType}
          onChange={(value) => dispatch(setChainType(value as 'all' | 'heavy' | 'light'))}
          data={CHAIN_DATA}
          classNames={{ root: s.chainRoot, indicator: s.chainIndicator, label: s.chainLabel }}
        />
      </div>
    </div>
  );
}
